import React from 'react';
import {
  Action, FormHandlers, Portal, useModal,
} from '@doveit/bricks';
import { Invoice } from '../../../providers/api/dtos';
import { InvoiceStatus } from '../../../domain/types';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import UpsertInvoice from '../upsert-invoice/UpsertInvoice';
import { markPaidInvoiceActionLabels } from './constants';
import { useComputedInvoiceStatus } from '../../hooks/use-computed-invoice-status/useComputedInvoiceStatus';

interface ChildFnProps {
  markPaidInvoice: VoidFunction,
}

export interface MarkPaidInvoiceActionProps extends React.AriaAttributes {
  invoice: Invoice,
  canEditInvoiceType?: boolean,
  onSuccess?: (invoice: Invoice) => void,
  toStatus: InvoiceStatus.PAID | InvoiceStatus.COLLECTED
  children?: (props: ChildFnProps) => React.ReactNode,
}

const MarkPaidInvoiceAction: React.FC<MarkPaidInvoiceActionProps> = ({
  invoice,
  canEditInvoiceType = false,
  onSuccess,
  toStatus,
  children,
}) => {
  const [formActionDisabled, setFormActionDisabled] = React.useState(false);
  const modal = useModal();
  const formRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;
  const { canBeUpdatedTo } = useComputedInvoiceStatus(invoice);

  const handleSuccess = React.useCallback((updatedInvoice: Invoice) => {
    modal.close();
    onSuccess?.(updatedInvoice);
  }, [modal, onSuccess]);

  if (!canBeUpdatedTo(toStatus)) {
    return null;
  }

  return (
    <>
      {children?.({ markPaidInvoice: modal.open }) || (
        <Action
          label={markPaidInvoiceActionLabels[toStatus].action.label}
          size="S"
          aria-label={markPaidInvoiceActionLabels[toStatus].action.ariaLabel}
          onClick={() => modal.open()}
        />
      )}

      <Portal>
        <SimpleModal
          {...modal}
          title={markPaidInvoiceActionLabels[toStatus].modal.title}
          aria-label={markPaidInvoiceActionLabels[toStatus].modal.ariaLabel}
          footer={(
            <Action
              label={markPaidInvoiceActionLabels[toStatus].modal.confirmLabel}
              color="primary"
              emphasis="high"
              disabled={formActionDisabled}
              aria-label={markPaidInvoiceActionLabels[toStatus].modal.confirmAriaLabel}
              onClick={() => formRef.current?.handleSubmit()}
            />
          )}
        >
          <UpsertInvoice
            formRef={formRef}
            invoice={invoice}
            toStatus={toStatus}
            onSuccess={handleSuccess}
            onFormStatusChanged={setFormActionDisabled}
            disabledFields={{
              type: !canEditInvoiceType,
              property: true,
              reference: true,
            }}
          />
        </SimpleModal>
      </Portal>
    </>
  );
};

export default MarkPaidInvoiceAction;
